/**
 * clothSolver.ts — pure CPU Verlet / position-based cloth for square sails.
 *
 * This module is deliberately free of THREE, DOM and WebGL so a plain Node
 * process can verify determinism and measure the solver without a browser
 * (see `scripts/check-cloth.mjs` and `scripts/bench-cloth.mjs`). It uses typed
 * arrays exclusively and performs no per-step allocation: every scratch buffer
 * is pre-allocated in `createGrid` and every helper writes into the existing
 * `ClothState`.
 *
 * Technique (Jakobsen Verlet + position-based distance constraints, the same
 * family as the three.js `webgl_animation_cloth` example):
 *
 *   pos' = (pos - prev) * (1 - damping) + pos + accel * dt^2
 *
 * then, per iteration, every structural/bend distance constraint is projected
 * back to its rest length. The head row is pinned (infinite mass) along the
 * yard so it never sags.
 *
 * Determinism: fixed timestep, seeded initialisation (mulberry32, see
 * `seededRandom` below), and no `Math.random`. Two clients that feed the same
 * wind/level/update sequence produce bit-identical positions.
 */

const TAU = Math.PI * 2;

/**
 * Default rest-belly depth (metres) baked into the sail's undeformed shape.
 * This is what makes a frozen/far sail read as billowing fabric instead of a
 * flat angular sheet: the head row stays on the yard while the belly deepens
 * toward the foot and the leech edges bow forward.
 */
export const DEFAULT_BELLY_DEPTH = 1.1;

/** Leech (side-edge) billow fraction relative to the sail centre (0..1). */
const LEECH_BILLOW = 0.2;

/** Foot rise (inverted-arch) as a fraction of the belly depth. */
const FOOT_RISE_RATIO = 0.32;

/**
 * Billowed rest shape for a square sail whose head row is pinned to the yard.
 *
 * The local X/Y grid stays rectangular while the Z (billow) axis and the foot
 * Y offset are sculpted so the sail reads as fabric even when frozen at
 * distance:
 *   - a strong centre belly (same `sin(π tx) · sin(π ty^0.55)` bubble family as
 *     the old static sail) for the curved shading,
 *   - a small leech billow so the side edges bow forward instead of staying
 *     ruler-straight,
 *   - a foot rise so the bottom edge forms a visible inverted arch from the
 *     chase camera.
 */
function restShape(
  tx: number,
  ty: number,
  depth: number,
): { x: number; y: number; z: number } {
  if (depth <= 0) return { x: 0, y: 0, z: 0 };
  const vertical = Math.sin(Math.PI * Math.pow(ty, 0.55));
  const widthProfile = LEECH_BILLOW + (1 - LEECH_BILLOW) * Math.sin(Math.PI * tx);
  const z = depth * vertical * widthProfile;
  const footRise = Math.sin(Math.PI * tx) * Math.pow(ty, 0.7);
  const y = depth * FOOT_RISE_RATIO * footRise;
  return { x: 0, y, z };
}

/**
 * Mulberry32, byte-for-byte the same generator as `src/utils/random.ts`.
 * Duplicated here (rather than imported) so this pure module stays importable
 * by Node's native TypeScript type-stripping without a bundler — Node cannot
 * resolve extension-less relative `.ts` imports, and `tsc` forbids the `.ts`
 * extension in project source.
 */
function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Tunable, shared solver parameters. All values are plain numbers. */
export interface ClothParams {
  /** Distance-constraint relaxation iterations per fixed step. */
  iterations: number;
  /** Velocity damping (0 = none). Drag is `1 - damping`. */
  damping: number;
  /** Constraint correction stiffness (0..1). */
  stiffness: number;
  /** Wind pressure gain — the billow term. */
  windGain: number;
  /** Back-pressure gain — the luff term (wind on the wrong side). */
  luffGain: number;
  /** High-frequency flutter gain. */
  flutterGain: number;
  /** Flutter frequency in Hz. */
  flutterFrequency: number;
  /** Gravity acceleration applied along -Y (m/s^2, already scaled down). */
  gravity: number;
  /** Wind-direction smoothing time constant (seconds). */
  windTau: number;
  /** Fixed simulation step (seconds). */
  fixedDt: number;
}

/**
 * Canonical tuned parameters. `iterations`/`fixedDt` come from the quality
 * preset; the remaining constants are shared so the Node check and benchmark
 * exercise exactly the same solver the game runs.
 */
export function clothParams(iterations: number, fixedDt: number): ClothParams {
  return {
    iterations,
    fixedDt,
    damping: 0.03,
    stiffness: 0.9,
    windGain: 8,
    luffGain: 4,
    flutterGain: 1.5,
    flutterFrequency: 6,
    gravity: 2.0,
    windTau: 0.8,
  };
}

/** Fully-owned, pre-allocated cloth state. */
export interface ClothState {
  /** Grid columns (cells across the width). */
  columns: number;
  /** Grid rows (cells down the height). */
  rows: number;
  /** Vertex count = (columns + 1) * (rows + 1). */
  count: number;
  /** Current positions, `count * 3` floats. */
  pos: Float32Array;
  /** Previous positions (Verlet), `count * 3` floats. */
  prev: Float32Array;
  /** Rest (undeformed) positions, `count * 3` floats. */
  rest: Float32Array;
  /** Inverse mass per vertex (0 = pinned/infinite mass). */
  invMass: Float32Array;
  /** 1 when the vertex is pinned to the yard, otherwise 0. */
  pinMask: Uint8Array;
  /** Per-vertex flutter phase in radians (seeded). */
  phase: Float32Array;
  /** Structural constraint index pairs (flat). */
  structural: Uint16Array;
  /** Rest length for each structural constraint. */
  structuralRest: Float32Array;
  /** Bend (skip-one) constraint index pairs (flat, may be empty). */
  bend: Uint16Array;
  /** Rest length for each bend constraint. */
  bendRest: Float32Array;
  /** Smoothed local wind (m/s-equivalent, magnitude = windStrength). */
  windLocal: Float32Array;
  /** Target local wind, written by `setWindTarget`. */
  windTarget: Float32Array;
  /** Per-step force accumulator, `count * 3` floats. */
  forces: Float32Array;
  /** Fractional sail level (0..1) scaling the wind force. */
  levelFraction: number;
  /** Simulation time in seconds. */
  time: number;
  /** Wall-clock accumulator for the fixed timestep. */
  acc: number;
  /** Seed used for the deterministic initialisation. */
  seed: number;
}

/**
 * Build a pinned square-sail grid. The sail spans `width` on local X and
 * `height` on local Y; local +Z is the leeward/billow direction. Row 0 (head,
 * +Y) is pinned to the yard. `withBend` adds skip-one bend constraints for the
 * higher-quality preset.
 */
export function createGrid(
  columns: number,
  rows: number,
  width: number,
  height: number,
  seed: number,
  withBend: boolean,
  bellyDepth: number = DEFAULT_BELLY_DEPTH,
): ClothState {
  const stride = columns + 1;
  const count = stride * (rows + 1);
  const random = seededRandom(seed);

  const pos = new Float32Array(count * 3);
  const prev = new Float32Array(count * 3);
  const rest = new Float32Array(count * 3);
  const invMass = new Float32Array(count);
  const pinMask = new Uint8Array(count);
  const phase = new Float32Array(count);

  for (let r = 0; r <= rows; r += 1) {
    for (let c = 0; c <= columns; c += 1) {
      const i = r * stride + c;
      const tx = columns > 0 ? c / columns : 0;
      const ty = rows > 0 ? r / rows : 0;
      const x = (tx - 0.5) * width;
      const y = (0.5 - ty) * height;
      const shape = restShape(tx, ty, bellyDepth);
      rest[i * 3] = x + shape.x;
      rest[i * 3 + 1] = y + shape.y;
      rest[i * 3 + 2] = shape.z;
      const pinned = r === 0 ? 1 : 0;
      pinMask[i] = pinned;
      invMass[i] = pinned ? 0 : 1;
      phase[i] = random() * TAU;
    }
  }

  // Start at rest with a tiny deterministic Z ripple so the cloth never sits
  // perfectly flat and the first wind shift reads as cloth instead of paper.
  for (let i = 0; i < count; i += 1) {
    pos[i * 3] = rest[i * 3];
    pos[i * 3 + 1] = rest[i * 3 + 1];
    pos[i * 3 + 2] = rest[i * 3 + 2];
    prev[i * 3] = rest[i * 3];
    prev[i * 3 + 1] = rest[i * 3 + 1];
    prev[i * 3 + 2] = rest[i * 3 + 2];
    if (!pinMask[i]) pos[i * 3 + 2] += (random() - 0.5) * 0.02;
  }

  const structural: number[] = [];
  for (let r = 0; r <= rows; r += 1) {
    for (let c = 0; c < columns; c += 1) {
      const i = r * stride + c;
      structural.push(i, i + 1);
    }
  }
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c <= columns; c += 1) {
      const i = r * stride + c;
      structural.push(i, i + stride);
    }
  }

  const bend: number[] = [];
  if (withBend) {
    for (let r = 0; r <= rows; r += 1) {
      for (let c = 0; c < columns - 1; c += 1) {
        const i = r * stride + c;
        bend.push(i, i + 2);
      }
    }
    for (let r = 0; r < rows - 1; r += 1) {
      for (let c = 0; c <= columns; c += 1) {
        const i = r * stride + c;
        bend.push(i, i + stride * 2);
      }
    }
  }

  const structuralArr = Uint16Array.from(structural);
  const bendArr = Uint16Array.from(bend);

  return {
    columns,
    rows,
    count,
    pos,
    prev,
    rest,
    invMass,
    pinMask,
    phase,
    structural: structuralArr,
    structuralRest: buildRestLengths(structuralArr, rest),
    bend: bendArr,
    bendRest: buildRestLengths(bendArr, rest),
    windLocal: new Float32Array(3),
    windTarget: new Float32Array(3),
    forces: new Float32Array(count * 3),
    levelFraction: 0,
    time: 0,
    acc: 0,
    seed,
  };
}

/** Compute rest lengths for a flat list of constraint index pairs. */
function buildRestLengths(pairs: Uint16Array, rest: Float32Array): Float32Array {
  const lengths = new Float32Array(pairs.length >> 1);
  for (let k = 0; k < pairs.length; k += 2) {
    const a = pairs[k] * 3;
    const b = pairs[k + 1] * 3;
    lengths[k >> 1] = Math.hypot(
      rest[b] - rest[a],
      rest[b + 1] - rest[a + 1],
      rest[b + 2] - rest[a + 2],
    );
  }
  return lengths;
}

/** Re-seed the simulation back to its deterministic rest pose. */
export function resetCloth(state: ClothState): void {
  const random = seededRandom(state.seed);
  for (let i = 0; i < state.count; i += 1) {
    state.pos[i * 3] = state.rest[i * 3];
    state.pos[i * 3 + 1] = state.rest[i * 3 + 1];
    state.pos[i * 3 + 2] = state.rest[i * 3 + 2];
    state.prev[i * 3] = state.rest[i * 3];
    state.prev[i * 3 + 1] = state.rest[i * 3 + 1];
    state.prev[i * 3 + 2] = state.rest[i * 3 + 2];
    if (!state.pinMask[i]) state.pos[i * 3 + 2] += (random() - 0.5) * 0.02;
  }
  state.windLocal[0] = 0;
  state.windLocal[1] = 0;
  state.windLocal[2] = 0;
  state.windTarget[0] = 0;
  state.windTarget[1] = 0;
  state.windTarget[2] = 0;
  state.time = 0;
  state.acc = 0;
}

/** Write the next local-wind target (already in sail-local space). */
export function setWindTarget(state: ClothState, x: number, y: number, z: number): void {
  state.windTarget[0] = x;
  state.windTarget[1] = y;
  state.windTarget[2] = z;
}

/**
 * Advance the cloth by exactly one fixed step of `params.fixedDt`. Allocation
 * free: it only touches the state's pre-allocated arrays and scalar locals.
 */
export function stepCloth(state: ClothState, params: ClothParams): void {
  const { columns, rows } = state;
  const stride = columns + 1;

  // Smooth the local wind toward its target with a first-order time constant.
  const blend = Math.min(1, params.fixedDt / params.windTau);
  const wl = state.windLocal;
  const wt = state.windTarget;
  wl[0] += (wt[0] - wl[0]) * blend;
  wl[1] += (wt[1] - wl[1]) * blend;
  wl[2] += (wt[2] - wl[2]) * blend;
  const windSpeed = Math.hypot(wl[0], wl[1], wl[2]);
  const level = state.levelFraction;

  const forces = state.forces;
  const pos = state.pos;
  const prev = state.prev;
  forces.fill(0);

  // Wind pressure + luff, accumulated per triangular face and scattered to its
  // three vertices (the three.js cloth-example model).
  if (windSpeed > 1e-4 && level > 0) {
    const wg = params.windGain;
    const lg = params.luffGain;
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < columns; c += 1) {
        const a = r * stride + c;
        const b = a + 1;
        const e = a + stride;
        const f = e + 1;
        // Triangle (a, e, b) then (b, e, f) — same winding as the rendered grid.
        applyFaceForce(forces, pos, a, e, b, wl, windSpeed, level, wg, lg);
        applyFaceForce(forces, pos, b, e, f, wl, windSpeed, level, wg, lg);
      }
    }
  }

  // Gravity and high-frequency flutter (applied along local +Z).
  const gravity = params.gravity;
  const flutterGain = params.flutterGain;
  const flutterFreq = params.flutterFrequency;
  const time = state.time;
  const phase = state.phase;
  const pinMask = state.pinMask;
  for (let i = 0; i < state.count; i += 1) {
    if (pinMask[i]) continue;
    forces[i * 3 + 1] -= gravity;
    if (windSpeed > 1e-4 && level > 0) {
      forces[i * 3 + 2] +=
        flutterGain * windSpeed * level * Math.sin(TAU * (phase[i] + time * flutterFreq));
    }
  }

  // Verlet integration.
  const drag = 1 - params.damping;
  const dt2 = params.fixedDt * params.fixedDt;
  const invMass = state.invMass;
  for (let i = 0; i < state.count; i += 1) {
    if (pinMask[i]) continue;
    const i3 = i * 3;
    const px = pos[i3];
    const py = pos[i3 + 1];
    const pz = pos[i3 + 2];
    const ox = prev[i3];
    const oy = prev[i3 + 1];
    const oz = prev[i3 + 2];
    prev[i3] = px;
    prev[i3 + 1] = py;
    prev[i3 + 2] = pz;
    pos[i3] = px + (px - ox) * drag + forces[i3] * dt2;
    pos[i3 + 1] = py + (py - oy) * drag + forces[i3 + 1] * dt2;
    pos[i3 + 2] = pz + (pz - oz) * drag + forces[i3 + 2] * dt2;
  }

  // Position-based distance-constraint relaxation.
  const stiffness = params.stiffness;
  for (let iter = 0; iter < params.iterations; iter += 1) {
    solveConstraints(pos, invMass, state.structural, state.structuralRest, stiffness);
    solveConstraints(pos, invMass, state.bend, state.bendRest, stiffness);
  }

  // Pinned head row never moves — snap it back to the yard each step.
  const rest = state.rest;
  for (let i = 0; i < state.count; i += 1) {
    if (!pinMask[i]) continue;
    const i3 = i * 3;
    pos[i3] = rest[i3];
    pos[i3 + 1] = rest[i3 + 1];
    pos[i3 + 2] = rest[i3 + 2];
    prev[i3] = rest[i3];
    prev[i3 + 1] = rest[i3 + 1];
    prev[i3 + 2] = rest[i3 + 2];
  }

  state.time += params.fixedDt;
}

/**
 * Accumulate the wind force for one triangle into its three vertices.
 * `windSpeed` is the local wind magnitude; the force follows
 * `k * max(0, n·w) * |w| * area * level` along the normal (and the mirrored
 * luff term along -n), the quadratic-in-speed form of the frozen wind model.
 */
function applyFaceForce(
  forces: Float32Array,
  pos: Float32Array,
  ai: number,
  bi: number,
  ci: number,
  wl: Float32Array,
  windSpeed: number,
  level: number,
  windGain: number,
  luffGain: number,
): void {
  const a = ai * 3;
  const b = bi * 3;
  const c = ci * 3;
  const abx = pos[b] - pos[a];
  const aby = pos[b + 1] - pos[a + 1];
  const abz = pos[b + 2] - pos[a + 2];
  const acx = pos[c] - pos[a];
  const acy = pos[c + 1] - pos[a + 1];
  const acz = pos[c + 2] - pos[a + 2];
  // cross(ab, ac)
  const nx = aby * acz - abz * acy;
  const ny = abz * acx - abx * acz;
  const nz = abx * acy - aby * acx;
  const len = Math.hypot(nx, ny, nz);
  if (len < 1e-9) return;
  const invLen = 1 / len;
  const ux = nx * invLen;
  const uy = ny * invLen;
  const uz = nz * invLen;
  const area = 0.5 * len;
  // n·w (units of m/s-equivalent), then |w|²-weighted via * windSpeed.
  const align = ux * wl[0] + uy * wl[1] + uz * wl[2];
  const third = 1 / 3;
  if (align > 0) {
    const mag = windGain * align * windSpeed * area * level * third;
    forces[a] += ux * mag;
    forces[a + 1] += uy * mag;
    forces[a + 2] += uz * mag;
    forces[b] += ux * mag;
    forces[b + 1] += uy * mag;
    forces[b + 2] += uz * mag;
    forces[c] += ux * mag;
    forces[c + 1] += uy * mag;
    forces[c + 2] += uz * mag;
  } else {
    const mag = luffGain * -align * windSpeed * area * level * third;
    forces[a] -= ux * mag;
    forces[a + 1] -= uy * mag;
    forces[a + 2] -= uz * mag;
    forces[b] -= ux * mag;
    forces[b + 1] -= uy * mag;
    forces[b + 2] -= uz * mag;
    forces[c] -= ux * mag;
    forces[c + 1] -= uy * mag;
    forces[c + 2] -= uz * mag;
  }
}

/** Project one flat list of distance constraints (Jakobsen relaxation). */
function solveConstraints(
  pos: Float32Array,
  invMass: Float32Array,
  pairs: Uint16Array,
  restLengths: Float32Array,
  stiffness: number,
): void {
  for (let k = 0; k < pairs.length; k += 2) {
    const a = pairs[k];
    const b = pairs[k + 1];
    const invA = invMass[a];
    const invB = invMass[b];
    const sum = invA + invB;
    if (sum <= 0) continue;
    const a3 = a * 3;
    const b3 = b * 3;
    const dx = pos[b3] - pos[a3];
    const dy = pos[b3 + 1] - pos[a3 + 1];
    const dz = pos[b3 + 2] - pos[a3 + 2];
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1e-9) continue;
    const corr = ((dist - restLengths[k >> 1]) / dist) * stiffness;
    const cx = dx * corr;
    const cy = dy * corr;
    const cz = dz * corr;
    const wa = invA / sum;
    const wb = invB / sum;
    pos[a3] += cx * wa;
    pos[a3 + 1] += cy * wa;
    pos[a3 + 2] += cz * wa;
    pos[b3] -= cx * wb;
    pos[b3 + 1] -= cy * wb;
    pos[b3 + 2] -= cz * wb;
  }
}

/** Maximum relative stretch error across the structural constraints. */
export function maxStretchError(state: ClothState): number {
  const { pos, structural, structuralRest } = state;
  let worst = 0;
  for (let k = 0; k < structural.length; k += 2) {
    const a = structural[k] * 3;
    const b = structural[k + 1] * 3;
    const dist = Math.hypot(pos[b] - pos[a], pos[b + 1] - pos[a + 1], pos[b + 2] - pos[a + 2]);
    const rest = structuralRest[k >> 1];
    if (rest <= 1e-9) continue;
    const error = Math.abs(dist / rest - 1);
    if (error > worst) worst = error;
  }
  return worst;
}

/** Deterministic 32-bit FNV-1a fingerprint of the cloth positions. */
export function hashCloth(state: ClothState): string {
  const bits = new Int32Array(state.pos.buffer, state.pos.byteOffset, state.pos.length);
  let hash = 0x811c9dc5;
  for (let i = 0; i < bits.length; i += 1) {
    hash ^= bits[i];
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}
